import { useUpdateCameraName, useUpdateConfiguration } from 'api/sdk';
import EditableLabel from 'components/EditableLabel';
import { useSnack } from 'plugins/snack';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface editableName {
  value: any;
  configId?: any;
  isRowEditing?: boolean;
  setIsRowEditing?: any;
  handletextClick?: any;
  style?: React.CSSProperties;
  refetch?: any;
  cameraId?: any;
  cameraValue?: any;
  type: 'config' | 'camera';
}

const EditName = ({
  value,
  configId,
  isRowEditing,
  setIsRowEditing,
  handletextClick,
  style,
  cameraId,
  type,
  refetch
}: editableName) => {
  const [isEditing, setEditing] = useState<boolean>(false);
  const [getName, setName] = useState<string>(value);
  const { data, mutateAsync, isLoading, reset } = useUpdateConfiguration();
  const { data: cameraData, mutateAsync: mutate } = useUpdateCameraName();
  const [inputError, setInputError] = useState<string | null>(null);
  const [currentValue, setCurrentValue] = useState<string>(value); // Add this to track current value
  const snack = useSnack();

  const currentConfigId = useSelector(
    (state: RootState) => state.configuration.configuration
  );

  // Function to strip all .mp4 extensions (case insensitive)
  const stripMP4Extension = (name: string): string => {
    // Replace all occurrences of .mp4 or .MP4 with empty string
    return name.replace(/\.mp4/gi, '');
  };

  // Function to ensure single .mp4 extension is present
  const ensureMP4Extension = (name: string): string => {
    // First remove all .mp4 extensions
    const cleanName = stripMP4Extension(name);
    // Add single .mp4 extension
    return `${cleanName}.mp4`;
  };

  // Initialize state when value prop changes
  useEffect(() => {
    setCurrentValue(value);
    setName(value);
  }, [value]);

  // Handle edit mode
  useEffect(() => {
    if (isEditing && type === 'camera') {
      setName(stripMP4Extension(currentValue));
    } else if (isEditing) {
      setName(currentValue);
    }
  }, [isEditing, type, currentValue]);
  const handleConfigName = async (name: string): Promise<void> => {
    await mutateAsync({
      params: {
        config_id: configId?.id,
        name: name
      }
    })
      .then(() => {
        refetch();
        setEditing(false);
        setIsRowEditing(false);
        setCurrentValue(name); // Update the current value
        setName(name);
        snack({
          message: 'Configuration name updated successfully',
          severity: 'success'
        });
      })
      .catch(error => {
        console.log('error', error);
        setInputError(error?.response?.data?.detail);
        snack({
          message: error?.response?.data?.detail,
          severity: 'error'
        });
      });
  };

  const handleVideoName = async (name: string): Promise<void> => {
    // Ensure the name has .mp4 extension before saving
    const finalName = ensureMP4Extension(name);
    await mutate({
      configId: currentConfigId,
      cameraId: cameraId?.id,
      data: {
        name: finalName
      }
    })
      .then(() => {
        setEditing(false);
        setIsRowEditing(false);
        setCurrentValue(finalName); // Update the current value
        setName(finalName);
        snack({
          message: 'Camera name updated successfully',
          severity: 'success'
        });
      })
      .catch(error => {
        console.log('error', error);
        setInputError(error?.response?.data?.detail);
        snack({
          message: error?.response?.data?.detail,
          severity: 'error'
        });
      });
  };
  const ValidationPatterns = {
    // For regular names (e.g., person names)
    PERSON_NAME: /^[a-zA-Z\s'-]+$/,
    // For usernames
    USERNAME: /^[a-zA-Z0-9_-]+$/,
    // For configuration/system names
    SYSTEM_NAME: /^[a-zA-Z][a-zA-Z0-9_-]*$/,
    // For display names (more permissive)
    DISPLAY_NAME: /^[a-zA-Z0-9\s_-]+$/
  };

  const handleValidation = (value: string) => {
    setInputError(null); // Reset error message

    if (!value) {
      snack({
        message: ' Name cannot be empty',
        severity: 'error'
      });
      return;
    }

    if (value.length < 4) {
      snack({
        message: 'Minimum length of the name must be 4 characters',
        severity: 'error'
      });
      return;
    }

    if (value.length > 20) {
      snack({
        message: 'Maximum length of the name can be 20 characters',
        severity: 'error'
      });
      return;
    }

    // Check for .mp4 in camera name input
    if (
      type === 'camera' &&
      (value.includes('.MP4') || value.includes('.mp4'))
    ) {
      snack({
        message: 'Please enter the name without .mp4 extension',
        severity: 'error'
      });
      return;
    }
    const regex = /^[a-zA-Z0-9][a-zA-Z0-9_-]*$/;
    if (!regex.test(value)) {
      snack({
        message:
          'Name can only contain letters, numbers, hyphens, and underscores',
        severity: 'error'
      });
      return;
    }

    if (type === 'config') {
      handleConfigName(value);
    } else if (type === 'camera') {
      handleVideoName(value);
    }
  };

  return (
    <div>
      <EditableLabel
        // gridView={gridView}
        style={style}
        value={getName}
        handletextClick={handletextClick}
        onChange={name => setName(name)}
        // loading={isLabelLoading}
        onCancel={() => {
          setIsRowEditing(false);
          setInputError(null);
          setEditing(false);
          setName(currentValue);
        }}
        onSave={() => handleValidation(getName)}
        error={''}
        isEditing={isEditing}
        setIsEditing={setEditing}
        fontSize={16}
        inputError={inputError}
        isRowEditing={isRowEditing}
        setIsRowEditing={setIsRowEditing}
      />
    </div>
  );
};

export default EditName;
